import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  User,
  LogOut,
  Award,
  Shield,
  Bell,
  Settings,
  Users,
  FileText,
  ChevronDown,
  Lock,
} from 'lucide-react';

const ProfileDropdown = ({
  onOpenSecuritySettings,
  onOpenNotificationPreferences,
  onOpenCredentials,
  onOpenStaffManagement,
  onOpenAuditLogs,
  onOpenProfile,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();

  // Defense-in-depth: if we're inside a demo org, never render a real user's
  // identity in the header. This blocks the paying-org identity leak that
  // happens when a super-admin views a demo tenant with their own JWT active.
  const isDemoOrg = Boolean(organization?.is_demo);
  const isDemoUser = Boolean(user?.is_demo);
  const maskIdentity = isDemoOrg && !isDemoUser;
  const displayName = maskIdentity ? 'Demo Admin' : (user?.full_name || user?.email?.split('@')[0] || 'User');
  const displayEmail = maskIdentity ? 'demo@counselcore.org' : (user?.email || '');
  const avatarChar = maskIdentity ? 'D' : (user?.full_name || user?.email || 'U').charAt(0).toUpperCase();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsOpen(false);
    logout();
    navigate('/login');
  };

  const handleAction = (callback) => {
    setIsOpen(false);
    callback?.();
  };

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'org_admin';

  const ROLE_LABELS = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    org_admin: 'Org Admin',
    therapist: 'Therapist',
    staff: 'Staff',
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
        data-testid="profile-dropdown-trigger"
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
          {avatarChar}
        </div>
        <div className="hidden sm:block text-left">
          <div className="text-sm font-medium text-gray-800 leading-tight">
            {displayName}
          </div>
          <div className="text-xs text-gray-500 leading-tight">
            {ROLE_LABELS[user?.role] || user?.role}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50 animate-in fade-in slide-in-from-top-2 duration-150"
             data-testid="profile-dropdown-menu">
          {/* User header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="text-sm font-semibold text-gray-900">{displayName}</div>
            <div className="text-xs text-gray-500 truncate">{displayEmail}</div>
          </div>

          {/* Profile */}
          <button
            onClick={() => handleAction(onOpenProfile)}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid="dropdown-profile"
          >
            <User className="w-4 h-4 text-gray-400" /> Profile
          </button>

          {/* Credentials (merged Professional + CYBER) */}
          <button
            onClick={() => handleAction(onOpenCredentials)}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid="dropdown-credentials"
          >
            <Award className="w-4 h-4 text-gray-400" /> Credentials
          </button>

          <button
            onClick={() => handleAction(onOpenSecuritySettings)}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid="dropdown-security"
          >
            <Shield className="w-4 h-4 text-gray-400" /> Security Settings
          </button>

          <button
            onClick={() => handleAction(onOpenNotificationPreferences)}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid="dropdown-notifications"
          >
            <Bell className="w-4 h-4 text-gray-400" /> Notification Preferences
          </button>

          {/* Admin-only items */}
          {isAdmin && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => handleAction(onOpenStaffManagement)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="dropdown-staff"
              >
                <Users className="w-4 h-4 text-gray-400" /> Staff Management
              </button>
              <button
                onClick={() => handleAction(onOpenAuditLogs)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="dropdown-audit"
              >
                <FileText className="w-4 h-4 text-gray-400" /> Audit Logs
              </button>
            </>
          )}

          {/* iter-77 · Lock now — force the idle-lock overlay without
              actually logging the user out.  Preserves in-flight work
              (drafts, half-written notes) while blurring the screen. */}
          <div className="border-t border-gray-100 my-1" />
          <button
            onClick={() => {
              setIsOpen(false);
              const forceLock = window.__counselcore_force_idle_lock;
              // iter-91 · pass a reason so the lock screen can tell the user
              // WHY they were locked (manual vs idle timeout)
              if (typeof forceLock === 'function') forceLock('manual');
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-indigo-700 hover:bg-indigo-50 transition-colors"
            data-testid="dropdown-lock-now"
          >
            <Lock className="w-4 h-4" /> Lock Screen
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
            data-testid="dropdown-logout"
          >
            <LogOut className="w-4 h-4" /> Log Out
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
